import type { LlmUIMessageParts } from '@azymmetric/shared/types/llm.types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { FolderTree } from 'lucide-react';
import { MessageContent } from '../../../../ai-elements/message';
import { Spinner } from '../../../../shared/spinner.component';

export type GetAllTechnologiesToolProps = {
  messageId: string;
  part: Extract<LlmUIMessageParts, { type: 'tool-getAllTechnologies' }>;
  partIndex: number;
};

export const GetAllTechnologiesTool: React.FC<GetAllTechnologiesToolProps> = (props) => {
  const { messageId, part, partIndex } = props;

  // Output available state
  if (part.state === 'output-available' && 'output' in part) {
    const allTechnologiesData = part.output;
    const count = allTechnologiesData.technologies.length;
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-3">
          <Accordion
            collapsible
            type="single"
          >
            <AccordionItem
              className="border-0"
              value="technologies"
            >
              <AccordionTrigger className="py-0 text-sm hover:no-underline">
                <div className="flex items-center gap-2">
                  <div className="rounded-md bg-[#3D2EFF1A] p-1.5 text-[#3D2EFF]">
                    <FolderTree className="size-3.5" />
                  </div>
                  <span>
                    Found {count} {count === 1 ? 'technology' : 'technologies'}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="max-h-96 space-y-2 mt-2 overflow-y-auto">
                  {allTechnologiesData.technologies.map((tech) => (
                    <div
                      className="rounded-md border bg-background p-3"
                      key={tech.id}
                    >
                      <div className="text-sm font-medium">{tech.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {tech.category.name} • {tech.company.name}
                      </div>
                      {tech.description && <p className="mt-1 text-xs text-muted-foreground">{tech.description}</p>}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </MessageContent>
    );
  }

  // Error state
  if (part.state === 'output-error' && 'errorText' in part) {
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <div className="text-sm text-destructive">Error retrieving technologies: {part.errorText}</div>
        </div>
      </MessageContent>
    );
  }

  // Loading state
  if (part.state === 'input-streaming' || part.state === 'input-available') {
    return (
      <MessageContent key={`${messageId}-${partIndex}`}>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-sm text-muted-foreground">Retrieving all technologies...</span>
          </div>
        </div>
      </MessageContent>
    );
  }

  return null;
};
